import { Field, LinkField, Text } from '@sitecore-jss/sitecore-jss-nextjs';
import { ComponentProps } from 'lib/component-props';
import ButtonSecondary from 'components/shared/Button/SecondaryButton';
import {
  SITE_MASTER_COLORS,
  StSiteMasterSection,
  StSiteMasterSectionTitle,
} from 'components/SiteMaster/SiteMaster.styles';
import {
  getFieldValue,
  useSiteMasterRouteFields,
  useSiteRecruitingStudiesProps,
} from 'components/SiteMaster/utils';
import { useTheme } from '@mui/material/styles';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/store/store';
import { withFindTrialsSiteParams } from 'lib/utils/findTrialsSiteParam';
import { beginFindTrialsSiteNavigation } from 'lib/utils/findTrialsSiteNavigation';

const FALLBACK_FIND_TRIALS_HREF = '/find-trials/search-result';
const FALLBACK_VIEW_ALL_STUDIES_BUTTON_TEXT = 'View All Studies';

type ExploreStudiesProps = ComponentProps & {
  fields: {
    sectionTitle?: Field<string>;
    sectionDescription?: Field<string>;
    viewAllStudiesButtonText?: Field<string>;
    viewAllStudiesButtonLink?: LinkField;
  };
};

const ExploreStudies = (props: ExploreStudiesProps): JSX.Element | null => {
  const theme = useTheme();
  const router = useRouter();
  const dispatch = useDispatch();
  const profileGender = useSelector(
    (state: RootState) => state.crProfile?.profileData?.basicDetails?.gender
  );
  // Single owner: SiteRecruitingStudies getStaticProps
  const { hasRecruitingStudies } = useSiteRecruitingStudiesProps();
  const routeFields = useSiteMasterRouteFields();

  if (!hasRecruitingStudies) {
    return null;
  }

  const sectionTitle = getFieldValue(props.fields.sectionTitle);
  const sectionDescription = getFieldValue(props.fields.sectionDescription);
  const viewAllStudiesButtonText = getFieldValue(props.fields.viewAllStudiesButtonText);
  const siteId = getFieldValue(routeFields?.siteId);
  const siteName = getFieldValue(routeFields?.siteName);
  let findTrialsHref = withFindTrialsSiteParams(
    props.fields.viewAllStudiesButtonLink?.value?.href || FALLBACK_FIND_TRIALS_HREF
  );
  const genderFacetValue =
    profileGender?.toLowerCase() === 'male'
      ? 'Male'
      : profileGender?.toLowerCase() === 'female'
        ? 'Female'
        : profileGender
          ? 'All'
          : '';
  if (genderFacetValue && !findTrialsHref.includes('f-trial_gender_list=')) {
    findTrialsHref = `${findTrialsHref}&f-trial_gender_list=${genderFacetValue}`;
  }

  const handleViewAllStudiesClick = (event: React.MouseEvent) => {
    if (!siteId) {
      return;
    }
    event.preventDefault();
    beginFindTrialsSiteNavigation(dispatch, { siteId, siteName });
    router.push(findTrialsHref);
  };

  return (
    <StSiteMasterSection
      id="explore-studies-card"
      sx={{ backgroundColor: SITE_MASTER_COLORS.accentSurface, borderRadius: '24px' }}
    >
      {sectionTitle && (
        <StSiteMasterSectionTitle
          sx={{
            fontFamily: `${theme.palette.secondaryFontFamilyType}, serif`,
            fontSize: '24px',
            fontWeight: 500,
            lineHeight: '100%',
            letterSpacing: 0,
            marginBottom: '12px',
          }}
        >
          <Text field={props.fields.sectionTitle} />
        </StSiteMasterSectionTitle>
      )}

      {sectionDescription && (
        <Text
          tag="p"
          field={props.fields.sectionDescription}
          style={{ marginBottom: 20, lineHeight: 1.4, fontSize: 14 }}
        />
      )}

      <ButtonSecondary
        href={findTrialsHref || undefined}
        onClick={siteId ? handleViewAllStudiesClick : undefined}
        style={{ width: '100%' }}
        aria-label={viewAllStudiesButtonText || FALLBACK_VIEW_ALL_STUDIES_BUTTON_TEXT}
      >
        {viewAllStudiesButtonText ? (
          <Text field={props.fields.viewAllStudiesButtonText} />
        ) : (
          FALLBACK_VIEW_ALL_STUDIES_BUTTON_TEXT
        )}
      </ButtonSecondary>
    </StSiteMasterSection>
  );
};

export default ExploreStudies;
